import React, { useCallback, useEffect, useRef } from 'react';
import {
  Badge,
  Box,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FiMessageSquare } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useTranslation } from 'i18n/TranslationProvider';
import { useGetUserRole } from 'domains/user/hooks/useGetUserRole';
import useUnreadMessageCount from 'domains/collaboration/hooks/useUnreadMessageCount';

export default function MessageNotificationBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { role } = useGetUserRole();
  const toast = useToast();
  const permissionRequestedRef = useRef(false);

  // Ask for browser Notification permission once (after login, non-admin).
  useEffect(() => {
    if (permissionRequestedRef.current) return;
    if (role === 'admin') return;
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
    permissionRequestedRef.current = true;
  }, [role]);

  const pathForListing = useCallback(
    (listingId) =>
      role === 'partner'
        ? `/listings/${listingId}?tab=messages`
        : `/my-listings/${listingId}?tab=messages`,
    [role],
  );

  const handleNewMessages = useCallback(
    (events) => {
      events.forEach((event) => {
        const title = event.title || t('New message');
        const description = event.text ? `${title}: ${event.text}` : title;

        // In-app toast (visible while the tab is focused).
        toast({
          title: t('New message'),
          description,
          status: 'info',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });

        // Native browser Notification (visible when tab is unfocused).
        if (
          typeof window !== 'undefined' &&
          'Notification' in window &&
          Notification.permission === 'granted' &&
          event.listingId
        ) {
          try {
            const n = new Notification(t('New message'), {
              body: description,
              tag: event.threadKey,
              icon: '/logo192.png',
            });
            n.onclick = () => {
              window.focus();
              navigate(pathForListing(event.listingId));
              n.close();
            };
          } catch {
            // ignore — permission edge cases / platform quirks
          }
        }
      });
    },
    [navigate, pathForListing, t, toast],
  );

  const { count, threads } = useUnreadMessageCount({
    enabled: role !== 'admin',
    onNewMessages: handleNewMessages,
  });

  const menuBg = useColorModeValue('white', 'navy.800');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const shadow = useColorModeValue(
    '14px 17px 40px 4px rgba(112, 144, 176, 0.18)',
    '14px 17px 40px 4px rgba(112, 144, 176, 0.06)',
  );
  const headerBorderColor = useColorModeValue('gray.100', 'gray.700');
  const unreadBg = useColorModeValue('blue.50', 'blue.900');
  const itemHoverBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const itemBorderColor = useColorModeValue('gray.50', 'gray.700');

  if (role === 'admin') return null;

  const handleThreadClick = (thread) => {
    const listingId = thread.listing?.id || thread.listing?._id;
    if (!listingId) return;
    const path =
      role === 'partner'
        ? `/listings/${listingId}?tab=messages`
        : `/my-listings/${listingId}?tab=messages`;
    navigate(path);
  };

  const formatTime = (value) => {
    if (!value) return '';
    try {
      return formatDistanceToNow(new Date(value), { addSuffix: true });
    } catch {
      return '';
    }
  };

  return (
    <Menu>
      <MenuButton p="0px" position="relative">
        <IconButton
          icon={<FiMessageSquare />}
          variant="ghost"
          color="gray.500"
          aria-label={t('Messages')}
          borderRadius="full"
        />
        {count > 0 && (
          <Badge
            position="absolute"
            top="4px"
            right="4px"
            colorScheme="red"
            borderRadius="full"
            fontSize="0.6em"
            px={1.5}
          >
            {count}
          </Badge>
        )}
      </MenuButton>
      <MenuList
        boxShadow={shadow}
        p="0px"
        mt="10px"
        borderRadius="20px"
        bg={menuBg}
        border="none"
        w="max-content"
        maxW="380px"
        maxH="400px"
        overflowY="auto"
      >
        <Flex w="100%" px={4} py={3} borderBottom="1px solid" borderColor={headerBorderColor}>
          <Text fontWeight="bold" color={textColor}>
            {t('Messages')}
          </Text>
        </Flex>

        {threads.length === 0 ? (
          <Box p={4} textAlign="center">
            <Text fontSize="sm" color="gray.500">
              {t('No unread messages')}
            </Text>
          </Box>
        ) : (
          threads.map((thread) => {
            const listingId = thread.listing?.id || thread.listing?._id;
            const route = [thread.listing?.route?.from, thread.listing?.route?.to]
              .filter(Boolean)
              .join(' \u2192 ');

            return (
              <MenuItem
                key={thread.threadKey || listingId}
                onClick={() => handleThreadClick(thread)}
                bg={unreadBg}
                _hover={{ bg: itemHoverBg }}
                px={4}
                py={3}
                borderBottom="1px solid"
                borderColor={itemBorderColor}
              >
                <Flex direction="column" w="100%" gap={1}>
                  <Flex justify="space-between" align="center" gap={2}>
                    <Text
                      fontSize="sm"
                      fontWeight="bold"
                      color={textColor}
                      noOfLines={1}
                      flex={1}
                    >
                      {thread.listing?.title || thread.listing?.humanId || t('Untitled listing')}
                    </Text>
                    {thread.unreadCount > 0 && (
                      <Badge
                        colorScheme="blue"
                        borderRadius="full"
                        fontSize="0.65em"
                        px={1.5}
                        flexShrink={0}
                      >
                        {thread.unreadCount}
                      </Badge>
                    )}
                  </Flex>
                  {route && (
                    <Text fontSize="xs" color={mutedColor} noOfLines={1}>
                      {route}
                    </Text>
                  )}
                  {thread.lastMessage?.text && (
                    <Text fontSize="sm" color={mutedColor} noOfLines={1}>
                      {thread.lastMessage.text}
                    </Text>
                  )}
                  <Text fontSize="xs" color={mutedColor}>
                    {formatTime(thread.lastMessage?.createdAt || thread.updatedAt)}
                  </Text>
                </Flex>
              </MenuItem>
            );
          })
        )}
      </MenuList>
    </Menu>
  );
}
